import React, { useState } from 'react';
import { rejectSubmission, verifySubmission } from '../lib/demographic';

/**
 * DemographicVerificationPanel
 *
 * Deliberately does NOT send verified_by or any actor identity in the
 * request body — per the server-side verification authority principle,
 * the backend derives the reviewer from the authenticated session
 * (req.platformUser), not from anything this component sends. This
 * component's only job is to call the two approve/reject endpoints and
 * show the result — it holds no authority of its own.
 */
export default function DemographicVerificationPanel({ submissionId, submission, onBack, onResolved }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleVerify() {
    setBusy(true);
    setError(null);
    try {
      await verifySubmission(submissionId);
      onResolved();
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (!rejectionReason.trim()) {
      setError('A rejection reason is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await rejectSubmission(submissionId, rejectionReason.trim());
      onResolved();
    } catch (err) {
      setError(err.response?.data?.message || 'Rejection failed');
    } finally {
      setBusy(false);
    }
  }

  if (!submission) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-xs text-slate-400 hover:text-white">
          ← Back to queue
        </button>
        <p className="text-sm text-red-400">Submission not found in the current queue view.</p>
      </div>
    );
  }

  const fields = [
    ['First name', submission.first_name],
    ['Last name', submission.last_name],
    ['Date of birth', submission.date_of_birth],
    ['Sex', submission.sex || '—'],
    ['External MRN', submission.external_mrn || '—'],
  ];

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-white">
        ← Back to queue
      </button>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
        <h3 className="text-lg font-bold text-white mb-4">
          Review: {submission.first_name} {submission.last_name}
        </h3>

        <dl className="grid grid-cols-2 gap-4 mb-6">
          {fields.map(([label, value]) => (
            <div key={label}>
              <dt className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                {label}
              </dt>
              <dd className="text-sm text-white mt-1">{value}</dd>
            </div>
          ))}
        </dl>

        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        {!showRejectForm ? (
          <div className="flex gap-3">
            <button
              onClick={handleVerify}
              disabled={busy}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition"
            >
              {busy ? 'Verifying…' : 'Verify'}
            </button>
            <button
              onClick={() => setShowRejectForm(true)}
              disabled={busy}
              className="flex-1 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 font-bold py-2.5 rounded-xl transition"
            >
              Reject
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection (required, shown to the patient)"
              rows={3}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-500"
            />
            <div className="flex gap-3">
              <button
                onClick={handleReject}
                disabled={busy}
                className="flex-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition"
              >
                {busy ? 'Rejecting…' : 'Confirm Rejection'}
              </button>
              <button
                onClick={() => setShowRejectForm(false)}
                disabled={busy}
                className="text-slate-400 hover:text-white px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}